import { api } from './api'
import { throwApiError } from './apiHelpers'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; error?: { code?: string }; data?: unknown }
  if (!b?.success) {
    const err = new Error(b?.message || 'Request failed') as Error & { code?: string }
    err.code = b.error?.code
    throw err
  }
  return b.data
}

export type SetupProgressStep = {
  id: string
  title: string
  complete: boolean
  optional?: boolean
}

export type StoreSetupProgress = {
  storeId: number
  storeName: string
  subdomain: string
  completedCount: number
  totalCount: number
  allComplete: boolean
  checklistDismissedAt: string | null
  steps: SetupProgressStep[]
}

export type OrganisationSetupProgress = {
  organisationId: number
  organisationStatus: string
  platform: {
    steps: SetupProgressStep[]
    completedCount: number
    totalCount: number
    allComplete: boolean
  }
  stores: StoreSetupProgress[]
}

export type GoLiveBlocker = {
  code: string
  message: string
  overridable: boolean
}

export type GoLiveReadiness = {
  storeId: number
  organisationId: number
  organisationStatus: string
  storePlatform: string
  alreadyLive: boolean
  ready: boolean
  blockers: GoLiveBlocker[]
  steps: SetupProgressStep[]
  completedRequiredCount: number
  requiredTotalCount: number
  billing: { required: boolean; status: string | null; ok: boolean }
  activated?: boolean
  message?: string
  forced?: boolean
}

export const setupProgressAdminApi = {
  async getOrganisationProgress(organisationId: string | number): Promise<OrganisationSetupProgress> {
    try {
      const { data } = await api.get(
        `/admin/organisations/${encodeURIComponent(String(organisationId))}/setup-progress`,
      )
      return assertSuccess(data) as OrganisationSetupProgress
    } catch (e) {
      throwApiError(e, 'Failed to load setup progress')
    }
  },

  async getStoreProgress(storeId: string | number): Promise<StoreSetupProgress> {
    try {
      const { data } = await api.get(`/admin/stores/${encodeURIComponent(String(storeId))}/setup-progress`)
      return assertSuccess(data) as StoreSetupProgress
    } catch (e) {
      throwApiError(e, 'Failed to load store setup progress')
    }
  },

  async getGoLiveReadiness(storeId: string | number): Promise<GoLiveReadiness> {
    try {
      const { data } = await api.get(
        `/admin/stores/${encodeURIComponent(String(storeId))}/go-live-readiness`,
      )
      return assertSuccess(data) as GoLiveReadiness
    } catch (e) {
      throwApiError(e, 'Failed to load go-live readiness')
    }
  },

  async postGoLive(storeId: string | number, force = false): Promise<GoLiveReadiness> {
    try {
      const { data } = await api.post(`/admin/stores/${encodeURIComponent(String(storeId))}/go-live`, {
        force,
      })
      return assertSuccess(data) as GoLiveReadiness
    } catch (e) {
      throwApiError(e, 'Failed to take store live')
    }
  },
}
